import React, { useContext, useState } from "react";

import PropTypes from "prop-types";
import { __RouterContext } from "react-router";

/* Emplacement, dans la barre de puces d'une page, ou une section deverse ses outils
 * (sous-vues, « + ») : evite une ligne d'en-tete de plus entre la barre et le tableau. */
export const BarreSlot = React.createContext(null);

/* Bandeau de page commun (gabarit de la map) : titre, outils a cote du titre (aside),
 * vues en pilules et actions a droite. */
function PageEntete({ title, items, value, onChange, label, actions, aside }) {
  return (
    <div className="cti-entete">
      <h1>{title}</h1>
      {aside && <div className="cti-entete-aside">{aside}</div>}
      {items && items.length > 0 && (
        <div className="cti-tabs" role="tablist" aria-label={label || title}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="tab"
              aria-selected={value === it.key}
              className={`cti-tab${value === it.key ? " on" : ""}`}
              onClick={() => onChange(it.key)}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
      {actions && <div className="cti-entete-actions">{actions}</div>}
    </div>
  );
}

/* Parametres de la page lus et ecrits dans l'URL, pour qu'un lien partage la vue.
 * update({ cle: valeur }) fusionne dans l'URL courante (null/undefined retire la cle) ;
 * plusieurs cles d'un coup restent coherentes (lecture de l'URL au moment de l'ecriture).
 * Hors routeur (composant rendu seul, tests unitaires), repli sur un etat local. */
export function useUrlParams() {
  const router = useContext(__RouterContext);
  const [local, setLocal] = useState({});
  if (!router) {
    return [local, (patch) => setLocal((prev) => ({ ...prev, ...patch }))];
  }
  const params = Object.fromEntries(new URLSearchParams(router.location.search));
  const update = (patch) => {
    const { history } = router;
    const next = new URLSearchParams(history.location.search);
    Object.entries(patch).forEach(([k, v]) =>
      v === null || v === undefined ? next.delete(k) : next.set(k, String(v))
    );
    history.replace(`${history.location.pathname}?${next}`);
  };
  return [params, update];
}

/* Vue courante (?tab= par defaut) ; valeur inconnue ou absente -> premiere vue. */
export function useUrlTab(keys, param = "tab") {
  const [params, update] = useUrlParams();
  const value = keys.includes(params[param]) ? params[param] : keys[0];
  return [value, (key) => update({ [param]: key })];
}

PageEntete.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node.isRequired })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  actions: PropTypes.node,
  aside: PropTypes.node,
};

/* Puces d'une barre blanche (sous-sections, sous-vues). */
export function Puces({ items, value, onChange, label, inline, extra }) {
  return (
    <div className={inline ? "cti-chips" : "cti-bar"} role="tablist" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="tab"
          aria-selected={value === it.key}
          className={`cti-chip${value === it.key ? " on" : ""}`}
          onClick={() => onChange(it.key)}
        >
          {it.label}
          {it.count !== undefined && <span className="cti-chip-n">{it.count}</span>}
        </button>
      ))}
      {extra}
    </div>
  );
}

Puces.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.number,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  inline: PropTypes.bool,
  extra: PropTypes.node,
};

export default PageEntete;
